<!-- 
Saved searches dropdown menu.

Saved searches allow the user to save a set of search criteria for use later.
The search criteria are saved against the user record in the database.
-->
<template>
  <v-menu v-if="isSignedIn" v-model="menuOpen" :close-on-content-click="false" offset-y>
    <v-btn 
      slot="activator" 
      :depressed="depressed" 
      :flat="flat" 
      :class="{'mb-3': !flat}" 
      :color="color"
      class="ml-0 mr-0"
    >
      <v-icon class="mr-2">save</v-icon>
      <span v-if="dense" class="hidden-xs-only">Saved searches</span>
      <template v-else>
        Saved searches
      </template>
      <our-expand-icon :expanded="menuOpen"/>
    </v-btn>
    <v-card>
      <v-list>
        <v-list-tile
          v-for="(savedSearch, index) in savedSearches"
          :key="savedSearch.name" class="secondary-list-item"
          @click="select(savedSearch)"
        >
          <v-list-tile-title class="mr-3">
            {{ savedSearch.name }}
          </v-list-tile-title>
          <v-list-tile-action>
            <v-tooltip top>
              <v-btn 
                slot="activator"
                :disabled="isFirst(index)" 
                :class="{'move-button': !isFirst(index), 'disabled-button': isFirst(index)}"
                class="edit-button"
                small flat icon 
                @click.stop="up(savedSearch)"
              >
                <v-icon small class="text-xs-right">arrow_upward</v-icon>
              </v-btn>
              <span v-if="isFirst(index)">(Already at the top of the list.)</span>
              <span v-else>Move this saved search up.</span>
            </v-tooltip>
            <v-tooltip top>
              <v-btn 
                slot="activator"
                :disabled="isLast(index)" 
                :class="{'move-button': !isLast(index), 'disabled-button': isLast(index)}"
                class="edit-button"
                small flat icon 
                @click.stop="down(savedSearch)"
              >
                <v-icon small class="text-xs-right">arrow_downward</v-icon>
              </v-btn>
              <span v-if="isLast(index)">(Already at the bottom of the list.)</span>
              <span v-else>Move this saved search down.</span>
            </v-tooltip>
            <v-tooltip top>
              <v-btn 
                slot="activator"
                small flat icon 
                class="edit-button remove-button" 
                @click.stop="remove(savedSearch)"
              >
                <v-icon small class="text-xs-right">clear</v-icon>
              </v-btn>
              <span>Delete this saved search.</span>
            </v-tooltip>
          </v-list-tile-action>
        </v-list-tile>
      </v-list>

      <template v-if="!showSave">
        <v-card-text v-if="!any" style="max-width: 400px;">
          You haven't saved any searches yet. Click on the save button below to save the current search to use again later.
        </v-card-text>
        <v-divider v-else />
        <v-card-actions class="ml-2 pb-1">
          <v-spacer/>
          <v-btn :disabled="alreadySaved" flat color="primary" @click="showSave=true">Save</v-btn>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-actions class="ml-2 pb-1">
          <v-form ref="form">
            <v-text-field 
              v-model="name"
              :rules="[rules.required, notDuplicated, maxLength]"
              label="Name" 
              hint="Choose a name for your saved search and hit enter."
              append-icon="save"
              outline persistent-hint autofocus
              @keydown.enter.stop="save"
              @click:append="save"
            />
          </v-form>
        </v-card-actions>
      </template>
    </v-card>
  </v-menu>
</template>

<script>
import _ from "underscore";
import * as action from "@/store/searches/action-types";
import * as mutation from "@/store/searches/mutation-types";
import ruleMixins from "@/mixins/rule.mixins";

export default {
  mixins: [ruleMixins],
  props: {
    module: {
      type: String,
      required: true
    },
    criteriaMutation: {
      type: String,
      required: true
    },
    clearCriteriaMutation: {
      type: String,
      required: true
    },
    criteria: {
      type: Object,
      required: true
    },
    depressed: {
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "default"
    },
    dense: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      name: "",
      menuOpen: false,
      showSave: false,
      alreadySaved: false,
      maxLength: name => (name.length > 50 ? "Maximum of 50 characters" : true)
    };
  },
  computed: {
    savedSearches() {
      return this.$store.state.searches.savedSearches.data[this.module];
    },
    isSignedIn() {
      return this.$store.getters.isSignedIn;
    },
    any() {
      return this.savedSearches.length > 0;
    },
    notDuplicated() {
      return name => {
        if (this.existingNames.includes(name)) {
          return "There is an existing saved search with this name";
        } else {
          return true;
        }
      };
    },
    existingNames() {
      return _(this.savedSearches).pluck("name") || [];
    }
  },
  watch: {
    menuOpen(nowOpen) {
      if (nowOpen) {
        this.showSave = false;
        this.name = "";
        this.alreadySaved = false;
      }
    }
  },
  mounted() {
    if (this.isSignedIn) {
      this.$store.dispatch(action.SAVED_SEARCHES_GET);
    }
  },
  methods: {
    save() {
      if (this.$refs.form.validate()) {
        this.$store.commit(mutation.SAVED_SEARCHES_ADD, {
          name: this.name,
          module: this.module,
          criteria: this.criteria
        });
        this.$store.dispatch(action.SAVED_SEARCHES_SAVE);
        this.alreadySaved = true;
        this.showSave = false;
      }
    },
    select(savedSearch) {
      this.menuOpen = false;
      // clear first in case we have added extra search criteria since the user saved their search
      this.$store.commit(this.clearCriteriaMutation);
      this.$store.commit(this.criteriaMutation, savedSearch.criteria);
      this.$emit("selected", savedSearch);
    },
    remove(savedSearch) {
      this.$store.commit(mutation.SAVED_SEARCHES_DELETE, { name: savedSearch.name, module: this.module });
      this.$store.dispatch(action.SAVED_SEARCHES_SAVE);
    },
    up(savedSearch) {
      this.$store.commit(mutation.SAVED_SEARCHES_MOVE_UP, { name: savedSearch.name, module: this.module });
      this.$store.dispatch(action.SAVED_SEARCHES_SAVE);
    },
    down(savedSearch) {
      this.$store.commit(mutation.SAVED_SEARCHES_MOVE_DOWN, { name: savedSearch.name, module: this.module });
      this.$store.dispatch(action.SAVED_SEARCHES_SAVE);
    },
    isFirst(index) {
      return index == 0;
    },
    isLast(index) {
      return index == this.savedSearches.length - 1;
    }
  }
};
</script>

<style lang="scss">
button.v-btn.edit-button {
  display: none;
}
.v-list__tile--link:hover {
  button.v-btn.move-button {
    display: inherit;
    color: var(--v-primary-base);
  }
  button.v-btn.remove-button {
    display: inherit;
    color: var(--v-error-base);
  }
  button.v-btn.disabled-button {
    display: inherit;
    color: gray;
  }
}
</style>